import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut,
  Plus,
  Trash2,
  Save,
  X,
  Loader2,
  ShieldCheck,
  ArrowLeft,
  Pencil,
  ExternalLink,
  Upload,
  Check,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  login,
  me,
  getToken,
  clearToken,
  listItems,
  createItem,
  updateItem,
  deleteItem,
  uploadFile,
  fileUrl,
  bulkUpsert,
} from "./api";
import { RESOURCES, findResource } from "./schemas";
import {
  CREDENTIALS,
  AWARDS,
  EXPERIENCES,
  PROJECTS,
  SKILLS,
  PLACES,
} from "../cv/data";

const STATIC_DEFAULTS = {
  credentials: CREDENTIALS,
  awards: AWARDS,
  experiences: EXPERIENCES,
  projects: PROJECTS,
  skills: SKILLS,
  places: PLACES,
};

/* ─── Field renderer ───────────────────────────────────────────────── */
const Field = ({ field, value, onChange }) => {
  const base =
    "w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[14px] text-white placeholder-white/30 focus:border-[#e6c769]/60 focus:outline-none transition-colors";

  if (field.type === "textarea") {
    return (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        rows={4}
        className={base}
        data-testid={`field-${field.key}`}
      />
    );
  }
  if (field.type === "select") {
    return (
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={base}
        data-testid={`field-${field.key}`}
      >
        {(field.options || []).map((o) => (
          <option key={o} value={o} style={{ background: "#0e1218" }}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === "bool") {
    return (
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 accent-[#e6c769]"
          data-testid={`field-${field.key}`}
        />
        <span className="text-[13px] text-white/70">Yes</span>
      </label>
    );
  }
  if (field.type === "tags") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <TagsField value={arr} onChange={onChange} placeholder={field.placeholder} testid={field.key} />
    );
  }
  if (field.type === "color") {
    return (
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value ?? "#e6c769"}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded border border-white/10 bg-transparent cursor-pointer"
          data-testid={`field-${field.key}`}
        />
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={base}
          placeholder="#e6c769"
        />
      </div>
    );
  }
  if (field.type === "number") {
    return (
      <input
        type="number"
        step="0.01"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
        placeholder={field.placeholder}
        className={base}
        data-testid={`field-${field.key}`}
      />
    );
  }
  if (field.type === "file" || field.type === "image") {
    return <FileField value={value} onChange={onChange} accept={field.accept} kind={field.type} testid={field.key} />;
  }
  return (
    <input
      type={field.type === "url" ? "url" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      className={base}
      data-testid={`field-${field.key}`}
    />
  );
};

const FileField = ({ value, onChange, accept, kind, testid }) => {
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [err, setErr] = React.useState("");
  const inputRef = React.useRef(null);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr("");
    setUploading(true);
    setProgress(0);
    try {
      const res = await uploadFile(f, setProgress);
      onChange(res.url);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const isImg = kind === "image" || (value && /\.(png|jpe?g|webp|gif)$/i.test(value)) || (value || "").includes("image");
  const preview = value ? fileUrl(value) : null;
  const defaultAccept = kind === "image" ? "image/*" : "image/*,application/pdf";

  return (
    <div className="space-y-2">
      {value && (
        <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-2">
          {isImg ? (
            <img
              src={preview}
              alt="preview"
              className="w-14 h-14 rounded object-cover border border-white/10"
            />
          ) : (
            <div className="w-14 h-14 rounded grid place-items-center bg-[#e6c769]/10 border border-[#e6c769]/30 text-[#e6c769]">
              <FileText className="w-5 h-5" />
            </div>
          )}
          <a
            href={preview}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 min-w-0 text-[12px] text-white/70 truncate hover:text-white transition-colors"
          >
            {value}
          </a>
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`file-remove-${testid}`}
            className="w-8 h-8 grid place-items-center rounded-full border border-white/10 text-red-300/70 hover:text-red-300 hover:border-red-500/40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <label
        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed transition-all cursor-pointer text-[12px] tracking-[0.2em] uppercase font-mono ${
          uploading
            ? "border-[#e6c769]/50 text-[#e6c769] bg-[#e6c769]/5"
            : "border-white/15 text-white/60 hover:border-[#e6c769]/50 hover:text-[#e6c769]"
        }`}
        data-testid={`file-upload-${testid}`}
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading… {progress}%
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            {value ? "Replace file" : kind === "image" ? "Upload image" : "Upload image or PDF"}
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept || defaultAccept}
          onChange={onPick}
          disabled={uploading}
          className="hidden"
        />
      </label>
      {err && <div className="text-[11px] text-red-300">{err}</div>}
    </div>
  );
};

const TagsField = ({ value, onChange, placeholder, testid }) => {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-[11px] text-white/75 font-mono"
          >
            {t}
            <button
              type="button"
              onClick={() => onChange(value.filter((x) => x !== t))}
              className="text-white/40 hover:text-white"
              data-testid={`tag-remove-${testid}-${t}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        placeholder={placeholder || "Type and press Enter"}
        className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white placeholder-white/30 focus:border-[#e6c769]/60 focus:outline-none transition-colors"
        data-testid={`field-${testid}`}
      />
    </div>
  );
};

/* ─── Login screen ─────────────────────────────────────────────────── */
const LoginScreen = ({ onSuccess }) => {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      onSuccess();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#06070b] text-white flex items-center justify-center p-6">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.01] backdrop-blur-2xl p-8 md:p-10"
        data-testid="admin-login-form"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.3em] uppercase font-mono text-white/40 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </Link>
        <div className="w-12 h-12 rounded-2xl grid place-items-center bg-[#e6c769]/10 border border-[#e6c769]/30 mb-5">
          <ShieldCheck className="w-5 h-5 text-[#e6c769]" />
        </div>
        <h1
          className="text-[32px] leading-tight text-white"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Admin Portal
        </h1>
        <p className="mt-2 text-[13px] text-white/55">
          Sign in to update your credentials, awards, experience, projects and skills.
        </p>

        <div className="mt-8 space-y-4">
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-[14px] text-white placeholder-white/30 focus:border-[#e6c769]/60 focus:outline-none"
              placeholder="you@example.com"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono mb-1.5">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-[14px] text-white placeholder-white/30 focus:border-[#e6c769]/60 focus:outline-none"
              placeholder="••••••••"
              data-testid="login-password"
            />
          </div>
        </div>

        {err && (
          <div
            className="mt-4 text-[12px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
            data-testid="login-error"
          >
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#e6c769] text-black text-[12px] tracking-[0.22em] uppercase font-medium hover:scale-[1.02] transition-transform disabled:opacity-50"
          data-testid="login-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </button>
      </motion.form>
    </div>
  );
};

/* ─── Item editor modal ────────────────────────────────────────────── */
const ItemEditor = ({ resource, item, onClose, onSaved }) => {
  const [form, setForm] = React.useState(
    item ? { ...item } : { ...(resource.defaults || {}) }
  );
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");

  const set = (key, v) => setForm((f) => ({ ...f, [key]: v }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      if (item?.id) {
        await updateItem(resource.key, item.id, form);
      } else {
        await createItem(resource.key, form);
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0 }}
        className="w-full max-w-2xl my-8 rounded-3xl border border-white/10 bg-gradient-to-br from-[#0e1218] to-[#06070b] p-8"
        data-testid="item-editor"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-[#e6c769]/80">
              {item ? "Editing" : "New"} · {resource.label}
            </div>
            <h2
              className="mt-2 text-[28px] leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {item ? "Edit item" : "Add new item"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/30"
            data-testid="editor-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-2">
          {resource.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-white/45 font-mono mb-1.5">
                {field.label}
              </label>
              <Field field={field} value={form[field.key]} onChange={(v) => set(field.key, v)} />
              {field.help && <p className="mt-1 text-[11px] text-white/40">{field.help}</p>}
            </div>
          ))}
        </div>

        {err && (
          <div className="mt-4 text-[12px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {err}
          </div>
        )}

        <div className="mt-6 flex items-center gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-full border border-white/15 text-white/70 text-[12px] tracking-[0.2em] uppercase font-mono hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#e6c769] text-black text-[12px] tracking-[0.2em] uppercase font-medium hover:scale-[1.03] transition-transform disabled:opacity-50"
            data-testid="editor-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {item ? "Save changes" : "Create"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* ─── Resource list panel ──────────────────────────────────────────── */
const ResourcePanel = ({ resource }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // null | 'new' | item object
  const [deleteId, setDeleteId] = React.useState(null);
  const [importing, setImporting] = React.useState(false);
  const [importResult, setImportResult] = React.useState(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await listItems(resource.key);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [resource.key]);

  React.useEffect(() => {
    refresh();
    setImportResult(null);
  }, [refresh]);

  const confirmDelete = async () => {
    await deleteItem(resource.key, deleteId);
    setDeleteId(null);
    refresh();
  };

  const importDefaults = async () => {
    if (importing) return;
    const defaults = STATIC_DEFAULTS[resource.key] || [];
    if (defaults.length === 0) return;
    if (!window.confirm(
      `Import ${defaults.length} site default ${resource.label.toLowerCase()} into the database?\n\nExisting entries with matching IDs will be UPDATED. New entries will be INSERTED.\n\nAfter this, everything becomes editable and deletable from the admin.`
    )) return;
    setImporting(true);
    try {
      const res = await bulkUpsert(resource.key, defaults);
      setImportResult(res);
      await refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const staticCount = (STATIC_DEFAULTS[resource.key] || []).length;
  const canImport = staticCount > items.length;
  const isImageResource = resource.key === "images";

  const renderCard = (item) => {
    const card = resource.card(item);

    if (isImageResource) {
      return (
        <div
          key={item.id}
          className="group relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors"
          data-testid={`item-${resource.key}-${item.id}`}
        >
          {item.url ? (
            <img
              src={fileUrl(item.url)}
              alt={item.title || ""}
              className="w-full aspect-[4/3] object-cover"
            />
          ) : (
            <div className="w-full aspect-[4/3] grid place-items-center text-white/25">
              <ImageIcon className="w-8 h-8" />
            </div>
          )}
          <div className="p-3">
            <div className="text-[13px] text-white truncate">{card.title || "Untitled"}</div>
            <div className="text-[11px] text-white/50 truncate mt-0.5">
              {card.subtitle}
              {card.meta ? (
                <>
                  <span className="mx-2 text-white/25">·</span>
                  <span className="font-mono">{card.meta}</span>
                </>
              ) : null}
            </div>
          </div>
          <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setEditing(item)}
              className="w-8 h-8 grid place-items-center rounded-full bg-black/70 border border-white/20 text-white/80 hover:text-white backdrop-blur-sm"
              data-testid={`btn-edit-${item.id}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setDeleteId(item.id)}
              className="w-8 h-8 grid place-items-center rounded-full bg-black/70 border border-white/20 text-red-300/80 hover:text-red-300 backdrop-blur-sm"
              data-testid={`btn-delete-${item.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        key={item.id}
        className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] hover:border-white/25 px-4 py-3 transition-colors"
        data-testid={`item-${resource.key}-${item.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[15px] text-white truncate">{card.title || "Untitled"}</div>
          <div className="text-[12px] text-white/50 truncate">
            {card.subtitle}
            {card.meta ? (
              <>
                <span className="mx-2 text-white/25">·</span>
                <span className="font-mono">{card.meta}</span>
              </>
            ) : null}
          </div>
        </div>
        <button
          onClick={() => setEditing(item)}
          className="opacity-0 group-hover:opacity-100 w-9 h-9 grid place-items-center rounded-full border border-white/10 text-white/60 hover:text-white transition-all"
          data-testid={`btn-edit-${item.id}`}
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={() => setDeleteId(item.id)}
          className="opacity-0 group-hover:opacity-100 w-9 h-9 grid place-items-center rounded-full border border-white/10 text-red-300/70 hover:text-red-300 hover:border-red-500/40 transition-all"
          data-testid={`btn-delete-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    );
  };

  return (
    <div data-testid={`resource-panel-${resource.key}`}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2
            className="text-[32px] leading-tight text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {resource.label}
          </h2>
          <p className="mt-1 text-[13px] text-white/55">{resource.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canImport && (
            <button
              onClick={importDefaults}
              disabled={importing}
              data-testid={`btn-import-${resource.key}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#e6c769]/40 text-[#e6c769] text-[11px] tracking-[0.22em] uppercase font-mono hover:bg-[#e6c769]/10 transition-colors disabled:opacity-50"
              title={`Import ${staticCount} pre-existing items into the database so you can edit them`}
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Importing…
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" /> Import site defaults ({staticCount})
                </>
              )}
            </button>
          )}
          <button
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#e6c769] text-black text-[11px] tracking-[0.22em] uppercase font-medium hover:scale-[1.03] transition-transform"
            data-testid={`btn-add-${resource.key}`}
          >
            <Plus className="w-4 h-4" /> Add new
          </button>
        </div>
      </div>

      {importResult && (
        <div
          className="mb-4 rounded-xl border border-[#e6c769]/30 bg-[#e6c769]/[0.06] px-4 py-3 text-[12.5px] text-[#e6c769]/90 flex items-center gap-2"
          data-testid="import-result"
        >
          <Check className="w-4 h-4" />
          Imported {importResult.total} items · <span className="font-mono">{importResult.inserted} new · {importResult.updated} updated</span>. All items are now editable below.
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 flex items-center justify-center text-white/40">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 border-dashed bg-white/[0.01] p-10 text-center">
          <div className="text-[13px] text-white/50">
            No {resource.label.toLowerCase()} yet. Click <strong className="text-white">Add new</strong> to
            create the first one.
          </div>
        </div>
      ) : (
        <div className={isImageResource ? "grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" : "grid gap-2"}>
          {items.map(renderCard)}
        </div>
      )}

      <AnimatePresence>
        {editing !== null && (
          <ItemEditor
            resource={resource}
            item={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteId && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="w-full max-w-md rounded-3xl border border-white/10 bg-gradient-to-br from-[#0e1218] to-[#06070b] p-8"
            >
              <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-red-300/80">
                Confirm delete
              </div>
              <h3
                className="mt-2 text-[22px]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Delete this item?
              </h3>
              <p className="mt-2 text-[13px] text-white/60">
                This cannot be undone.
              </p>
              <div className="mt-6 flex items-center gap-3 justify-end">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-5 py-2.5 rounded-full border border-white/15 text-white/70 text-[12px] tracking-[0.2em] uppercase font-mono hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-500 text-white text-[12px] tracking-[0.2em] uppercase font-medium hover:scale-[1.03] transition-transform"
                  data-testid="confirm-delete"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── Admin shell ──────────────────────────────────────────────────── */
const AdminApp = () => {
  const [auth, setAuth] = React.useState(null); // null=checking, false=out, {email}=in
  const [tab, setTab] = React.useState(RESOURCES[0].key);

  React.useEffect(() => {
    if (!getToken()) {
      setAuth(false);
      return;
    }
    me()
      .then((u) => setAuth(u))
      .catch(() => setAuth(false));
  }, []);

  const logout = () => {
    clearToken();
    setAuth(false);
  };

  if (auth === null) {
    return (
      <div className="min-h-screen bg-[#06070b] grid place-items-center text-white/40">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!auth) return <LoginScreen onSuccess={() => setAuth({ email: "…" })} />;

  const current = findResource(tab);

  return (
    <div className="min-h-screen bg-[#06070b] text-white">
      {/* Ambient noise/grain */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.03] mix-blend-overlay"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#06070b]/70 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full grid place-items-center bg-black/40 border border-[#e6c769]/30 overflow-hidden">
              <img src="/favicon.png" alt="UB" className="w-7 h-7 object-contain" />
            </div>
            <div className="leading-tight">
              <div className="text-[13px] font-medium tracking-wide">Personal Archive · Admin</div>
              <div className="text-[10px] text-white/50 tracking-[0.2em] uppercase">{auth.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-white/70 text-[11px] tracking-[0.2em] uppercase font-mono hover:text-white hover:border-white/30"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View site
            </Link>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-white/70 text-[11px] tracking-[0.2em] uppercase font-mono hover:text-white hover:border-white/30"
              data-testid="admin-logout"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Tab bar */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-6">
        <div className="flex flex-wrap gap-1.5">
          {RESOURCES.map((r) => {
            const isActive = r.key === tab;
            return (
              <button
                key={r.key}
                onClick={() => setTab(r.key)}
                data-testid={`tab-${r.key}`}
                className={`text-[11px] tracking-[0.22em] uppercase font-mono px-4 py-2 rounded-full border transition-all ${
                  isActive
                    ? "bg-[#e6c769] text-black border-[#e6c769]"
                    : "text-white/55 hover:text-white border-white/10 hover:border-white/25"
                }`}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Panel */}
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <ResourcePanel key={current.key} resource={current} />
      </main>
    </div>
  );
};

export default AdminApp;
